import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import ChartJS from "chart.js/auto";
import LoadingSquare from "shared/components/loading/LoadingSquare";
const ChartBarClubsRanking = ({ clubs, clubs2 = [], criteria = "mmr", highlightedClubs = [], highlightedClubColor = "rgba(255, 255, 255, 0.7)" }) => {
  /* * Main clubs determine the ranking/order. */ const sortedClubs = useMemo(() => {
    if (!clubs) return [];
    return [...clubs].sort((a, b) => (b[criteria] ?? 0) - (a[criteria] ?? 0));
  }, [clubs, criteria]);
  /* * clubs2 is independently sorted. * * We don't match clubs by ID. * Position 0 of clubs2 is compared with * position 0 of clubs, etc. */ const sortedClubs2 =
    useMemo(() => {
      if (!clubs2 || clubs2.length === 0) return [];
      return [...clubs2].sort((a, b) => (b[criteria] ?? 0) - (a[criteria] ?? 0));
    }, [clubs2, criteria]);
  if (!clubs || sortedClubs.length === 0) {
    return <LoadingSquare />;
  }
  const hasComparison = sortedClubs2.length > 0;
  /* * Labels always come from clubs. */ const labels = sortedClubs.map((club) => club.name);
  const values = sortedClubs.map((club) => club[criteria] ?? 0);
  /* * Comparison values are positional. */ const values2 = sortedClubs.map(
    (_, index) => sortedClubs2[index]?.[criteria] ?? null,
  );
  /* * Use both datasets when calculating the X axis. */ const allValues = hasComparison
    ? [...values, ...values2.filter((v) => v !== null)]
    : values;
  const minValue = Math.max(0, Math.floor(Math.min(...allValues) - 1));
  const maxValue = Math.floor(Math.max(...allValues)) + 1;
  /* * Highlighted clubs. */ const backgroundColors = sortedClubs.map((club) =>
    highlightedClubs.includes(club.id)
      ? highlightedClubColor
      : !clubs2 || clubs2.length === 0
        ? "rgba(248, 98, 133, 1)"
        : "rgba(248, 98, 133, 0.7)",
  );
  /* * When there is NO comparison, use the normal * Chart.js dataset exactly as before. * * This guarantees the original spacing/appearance. */
  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: backgroundColors,
        barPercentage: 1,
        categoryPercentage: 1,
        /* * Only used when there is comparison data. * With one dataset it has no effect. */ grouped: false,
      },
      ...(hasComparison
        ? [{ data: values2, backgroundColor: "#6c757d", barPercentage: 1, categoryPercentage: 1, grouped: false }]
        : []),
    ],
  };
  /* * Draw the comparison bar manually. * * This is what allows us to have: * * BLUE ███████████████ * GREY ███████████ * * without changing the vertical spacing between clubs. */
  const comparisonBarsPlugin = {
    id: "comparisonBars",
    beforeDatasetsDraw: (chart) => {
      if (!hasComparison) return;
      const currentMeta = chart.getDatasetMeta(0);
      if (!currentMeta || !currentMeta.data) {
        return;
      }
      const { ctx } = chart;
      const xScale = chart.scales.x;
      /* * Height of the original bar. * * We make each comparison bar slightly smaller * so both fit in the exact same category. */ const barHeight =
        currentMeta.data[0]?.height ?? 10;
      const comparisonBarHeight = barHeight * 0.45;
      currentMeta.data.forEach((currentBar, index) => {
        const comparisonValue = values2[index];
        if (comparisonValue === null || comparisonValue === undefined) {
          return;
        }
        /* * The center of the original bar. */ const centerY = currentBar.y;
        /* * Put the comparison bar underneath. */ const comparisonY = centerY + barHeight * 0.27;
        /* * Start and end X coordinates. */ const xStart = xScale.getPixelForValue(minValue);
        const xEnd = xScale.getPixelForValue(comparisonValue);
        const width = xEnd - xStart;
        ctx.save();
        ctx.fillStyle = "#6c757d";
        ctx.fillRect(xStart, comparisonY - comparisonBarHeight / 2, width, comparisonBarHeight);
        ctx.restore();
      });
    },
  };
  /* * Percentile labels. */ const topLeftLabelsPlugin = {
    id: "topLeftLabels",
    afterDatasetsDraw: (chart) => {
      const dataset = chart.data.datasets[0]?.data;
      if (!dataset || dataset.length === 0) {
        return;
      }
      const sortedValues = [...dataset].sort((a, b) => a - b);
      const q1 = sortedValues[Math.floor(sortedValues.length * 0.25)];
      const median = sortedValues[Math.floor(sortedValues.length * 0.5)];
      const q3 = sortedValues[Math.floor(sortedValues.length * 0.75)];
      const { ctx } = chart;
      ctx.save();
      ctx.fillStyle = "white";
      ctx.font = "14px Saira";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(`Top 25%: ${q3.toFixed(2)}`, 24, 15);
      ctx.fillText(`Top 50%: ${median.toFixed(2)}`, 24, 36);
      ctx.fillText(`Top 75%: ${q1.toFixed(2)}`, 24, 57);
      ctx.restore();
    },
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `${context.dataset.label ?? "Value"}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: { min: minValue, max: maxValue, grid: { display: false } },
      y: { grid: { display: false }, ticks: { display: false } },
    },
    layout: { padding: { top: 5 } },
  };
  return <Bar data={data} options={options} plugins={[comparisonBarsPlugin, topLeftLabelsPlugin]} />;
};
export default ChartBarClubsRanking;
